import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Fingerprint, Plus, Trash2, ShieldCheck, AlertTriangle, Loader2, Clock, LogOut, Monitor,
} from 'lucide-react';
import { crearPasskey, errorPasskey, passkeysSoportadas } from '../lib/webauthn';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fecha = (v) => (v ? new Date(v).toLocaleString('es-AR') : '—');

export default function Seguridad() {
  const [passkeys, setPasskeys] = useState([]);
  const [sesiones, setSesiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registrando, setRegistrando] = useState(false);
  const [nombre, setNombre] = useState('');

  const cargar = useCallback(async () => {
    try {
      const [pk, se] = await Promise.all([
        axios.get(`${API}/passkeys`, auth()),
        axios.get(`${API}/passkeys/sesiones`, auth()),
      ]);
      setPasskeys(pk.data.passkeys || []);
      setSesiones(se.data.sesiones || []);
    } catch {
      toast.error('No se pudieron leer tus dispositivos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cerrarOtras = async () => {
    if (!window.confirm('¿Cerrar todas las sesiones menos esta? Cualquier otro equipo tendrá que entrar de nuevo con su huella.')) return;
    try {
      const { data } = await axios.delete(`${API}/passkeys/sesiones`, auth());
      toast.success(data.cerradas > 0
        ? `${data.cerradas} sesión(es) cerrada(s)`
        : 'No había otras sesiones abiertas');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cerrar');
    }
  };

  const registrar = async () => {
    if (!nombre.trim()) return toast.error('Ponele un nombre al dispositivo');
    setRegistrando(true);
    try {
      const { data: o } = await axios.post(`${API}/passkeys/register/options`, {}, auth());
      const credential = await crearPasskey(o.options);
      const { data } = await axios.post(`${API}/passkeys/register/verify`,
        { token: o.token, credential, nombre: nombre.trim() }, auth());
      toast.success(`Passkey de "${data.nombre}" registrada`);
      setNombre('');
      cargar();
    } catch (e) {
      if (e?.response) toast.error(e.response.data?.detail || 'No se pudo registrar');
      else toast.error(errorPasskey(e));
    } finally {
      setRegistrando(false);
    }
  };

  const revocar = async (p) => {
    if (!window.confirm(`¿Quitar la passkey de "${p.nombre}"? Ese dispositivo dejará de poder entrar.`)) return;
    try {
      const { data } = await axios.delete(`${API}/passkeys/${p.id}`, auth());
      toast.success(data.restantes === 0
        ? 'Passkey quitada. Ese dispositivo ya no puede entrar con huella.'
        : 'Passkey quitada');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo quitar');
    }
  };

  const activo = passkeys.length > 0;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="seguridad-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-violet-500" /> Seguridad
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Passkeys: entrás con la huella del dispositivo, sin escribir la contraseña
        </p>
      </div>

      <div className={`rounded-lg border p-4 ${activo
        ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20'
        : 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'}`}>
        <p className={`text-sm font-semibold flex items-center gap-2 ${activo
          ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'}`}
          data-testid="seguridad-estado">
          {activo ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {activo
            ? `Sólo huella · ${passkeys.length} dispositivo(s) autorizado(s)`
            : 'Sin candado · la cuenta entra con contraseña'}
        </p>
        <p className="text-xs mt-1 text-gray-600 dark:text-gray-300">
          {activo
            ? 'La contraseña ya NO alcanza para entrar: sólo se entra desde un dispositivo autorizado. Cada ingreso queda registrado con el nombre del dispositivo y te aviso por Telegram si aparece una IP nueva.'
            : 'Registrá este dispositivo para poder entrar con la huella. Mientras no haya ninguno, se entra con contraseña.'}
        </p>
        {activo && passkeys.length < 2 && (
          <p className="text-xs mt-2 font-semibold text-amber-700 dark:text-amber-400">
            Tenés un solo dispositivo. Registrá al menos otro: si perdés este, hay que
            desbloquear la cuenta desde el servidor.
          </p>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-4 space-y-3">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Agregar este dispositivo</p>
        {!passkeysSoportadas() ? (
          <p className="text-sm text-rose-600">Este navegador no soporta passkeys.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nombre del dispositivo</label>
                <input value={nombre} onChange={(e) => setNombre(e.target.value)}
                  placeholder="MacBook de Nico" maxLength={60} data-testid="seguridad-nombre"
                  className="px-2 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 w-56" />
              </div>
              <button onClick={registrar} disabled={registrando} data-testid="seguridad-registrar"
                className="px-4 py-1.5 text-sm font-semibold rounded-lg bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 flex items-center gap-2">
                {registrando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Registrar con huella
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Poné un nombre que identifique quién usa el equipo: así en el registro de ingresos
              se ve qué dispositivo entró. Hay que hacerlo <b>desde cada dispositivo</b>.
            </p>
          </>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden">
        <div className="px-3 py-2 border-b dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Dispositivos autorizados</h2>
        </div>
        {loading ? (
          <p className="p-6 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></p>
        ) : passkeys.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">Todavía no registraste ninguno.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Dispositivo</th>
                <th className="px-3 py-2 text-left">Registrado</th>
                <th className="px-3 py-2 text-left">Último uso</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {passkeys.map((p) => (
                <tr key={p.id} className="border-t dark:border-gray-700" data-testid={`passkey-row-${p.id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Fingerprint className="w-4 h-4 text-violet-500" /> {p.nombre}
                    {p.backed_up && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                        title="Se sincroniza con la nube del fabricante (iCloud, Google)">sincronizada</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fecha(p.created_at)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {fecha(p.last_used_at)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => revocar(p)} data-testid={`passkey-revocar-${p.id}`}
                      className="text-rose-600 hover:text-rose-800 dark:hover:text-rose-400" title="Quitar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden">
        <div className="px-3 py-2 border-b dark:border-gray-700 flex items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sesiones abiertas</h2>
            <p className="text-xs text-gray-500">Si algo no te cierra, cerrá todas y volvé a entrar con la huella.</p>
          </div>
          <button onClick={cerrarOtras} data-testid="cerrar-otras-sesiones"
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-1.5 shrink-0">
            <LogOut className="w-3.5 h-3.5" /> Cerrar las demás
          </button>
        </div>
        {sesiones.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">
            {loading ? 'Cargando…' : 'No hay sesiones registradas todavía.'}
          </p>
        ) : (
          <ul className="divide-y dark:divide-gray-700">
            {sesiones.slice().reverse().map((s) => (
              <li key={s.sid} className="px-3 py-2 flex items-center gap-3" data-testid={`sesion-${s.sid}`}>
                <Monitor className="w-4 h-4 text-gray-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {s.dispositivo}
                    {s.actual && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        esta sesión
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {s.ip || 'sin IP'} · {fecha(s.created_at)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

    </div>
  );
}
